import { useEffect, useState } from 'react';
import { useTranslation } from '../../i18n';
import { api } from '../../lib/api';
import { PhotoUpload } from '../../components/PhotoUpload';
import { AddressInput } from '../../components/AddressInput';
import { parseAddress, stringifyAddress } from '../../lib/formatters';
import { CreateOrganisationModal, MultiInput } from './CreateOrganisationModal';

const ORG_TYPES = ['partner', 'funder', 'government', 'ngo', 'university', 'media', 'corporate', 'other'];

const FILTERS = [
  { key: 'all', labelKey: 'organisations.filters.all' },
  ...ORG_TYPES.map((type) => ({ key: type, type, labelKey: `organisations.filters.${type}` })),
];

const SOCIAL_ICONS = {
  linkedin:  '💼',
  instagram: '📷',
  facebook:  '👍',
  twitter:   '🐦',
};

const MOCK_ORGANISATIONS = [
  { id: 'mock-org-1', name: 'UNESCO',        type: 'partner',    website: 'https://unesco.org',     socialHandles: { linkedin: '@UNESCO_PL',     instagram: '@unesco_polska' } },
  { id: 'mock-org-2', name: 'Europa Nostra', type: 'partner',    website: 'https://europanostra.org', socialHandles: { linkedin: '@EuropaNostra' } },
  { id: 'mock-org-3', name: 'MKiDN',         type: 'government', website: 'https://gov.pl/kultura', socialHandles: { linkedin: '@MKiDN_RP' } },
];

// ─────────────────────────────────────────────────────────────────────────────
// Inline edit form (same fields as the create modal)
// ─────────────────────────────────────────────────────────────────────────────
function OrganisationEditForm({ organisation, onSave, onCancel, onDelete, saving, error }) {
  const { t } = useTranslation();
  const [logoB64, setLogoB64] = useState(organisation.logoB64 || null);
  const [name, setName] = useState(organisation.name || '');
  const [type, setType] = useState(organisation.type || 'partner');
  const [description, setDescription] = useState(organisation.description || '');
  const [website, setWebsite] = useState(organisation.website || '');
  const [emails, setEmails] = useState(organisation.emails?.length ? organisation.emails : ['']);
  const [phones, setPhones] = useState(organisation.phones?.length ? organisation.phones : ['']);
  const [address, setAddress] = useState(() => parseAddress(organisation.address));
  const [linkedin, setLinkedin] = useState(organisation.socialHandles?.linkedin || '');
  const [instagram, setInstagram] = useState(organisation.socialHandles?.instagram || '');
  const [facebook, setFacebook] = useState(organisation.socialHandles?.facebook || '');
  const [notes, setNotes] = useState(organisation.notes || '');

  const trimmedName = name.trim();
  const canSubmit = trimmedName.length > 0 && !saving;

  function handleSubmit(event) {
    event.preventDefault();
    if (!canSubmit) return;
    const handles = {};
    if (linkedin.trim())  handles.linkedin  = linkedin.trim();
    if (instagram.trim()) handles.instagram = instagram.trim();
    if (facebook.trim())  handles.facebook  = facebook.trim();
    onSave({
      name: trimmedName,
      type,
      description: description.trim() || null,
      logoB64: logoB64 || null,
      website: website.trim() || null,
      emails: emails.map((e) => e.trim()).filter(Boolean),
      phones: phones.map((p) => p.trim()).filter(Boolean),
      address: stringifyAddress(address),
      socialHandles: handles,
      notes: notes.trim() || null,
    });
  }

  return (
    <form className="person-edit" onSubmit={handleSubmit} noValidate>
      <div className="form-section">
        <h3 className="form-section__title">{t('organisations.form.sections.identity')}</h3>
        <div className="person-photo-row">
          <PhotoUpload value={logoB64} onChange={setLogoB64} shape="square" size={80} />
          <label className="form-field person-photo-row__field">
            <span className="form-field__label">{t('organisations.form.name')} *</span>
            <input
              className="form-field__input"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </label>
        </div>

        <label className="form-field">
          <span className="form-field__label">{t('organisations.form.type')}</span>
          <select
            className="form-field__select"
            value={type}
            onChange={(e) => setType(e.target.value)}
          >
            {ORG_TYPES.map((typeKey) => (
              <option key={typeKey} value={typeKey}>{t(`organisations.types.${typeKey}`)}</option>
            ))}
          </select>
        </label>

        <label className="form-field">
          <span className="form-field__label">{t('organisations.form.description')}</span>
          <textarea
            className="form-field__textarea"
            value={description}
            placeholder={t('organisations.form.descriptionPlaceholder')}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
          />
        </label>
      </div>

      <div className="form-section">
        <h3 className="form-section__title">{t('organisations.form.sections.contact')}</h3>
        <label className="form-field">
          <span className="form-field__label">{t('organisations.form.website')}</span>
          <input
            className="form-field__input"
            type="url"
            value={website}
            placeholder={t('organisations.form.websitePlaceholder')}
            onChange={(e) => setWebsite(e.target.value)}
          />
        </label>
        <MultiInput
          labelKey="organisations.form.emails"
          placeholderKey="organisations.form.emailPlaceholder"
          addLabelKey="organisations.form.addEmail"
          type="email"
          values={emails}
          onChange={setEmails}
        />
        <MultiInput
          labelKey="organisations.form.phones"
          placeholderKey="organisations.form.phonePlaceholder"
          addLabelKey="organisations.form.addPhone"
          type="tel"
          values={phones}
          onChange={setPhones}
        />
        <div className="form-field">
          <span className="form-field__label">{t('organisations.form.address')}</span>
          <AddressInput value={address} onChange={setAddress} />
        </div>
      </div>

      <div className="form-section">
        <h3 className="form-section__title">{t('organisations.form.sections.social')}</h3>
        <div className="form-field__stack">
          <div className="social-input">
            <span className="social-input__icon" aria-hidden="true">💼</span>
            <input className="form-field__input" type="text" value={linkedin} placeholder={t('organisations.form.linkedinPlaceholder')} onChange={(e) => setLinkedin(e.target.value)} />
          </div>
          <div className="social-input">
            <span className="social-input__icon" aria-hidden="true">📷</span>
            <input className="form-field__input" type="text" value={instagram} placeholder={t('organisations.form.instagramPlaceholder')} onChange={(e) => setInstagram(e.target.value)} />
          </div>
          <div className="social-input">
            <span className="social-input__icon" aria-hidden="true">👍</span>
            <input className="form-field__input" type="text" value={facebook} placeholder={t('organisations.form.facebookPlaceholder')} onChange={(e) => setFacebook(e.target.value)} />
          </div>
        </div>
      </div>

      <div className="form-section">
        <h3 className="form-section__title">{t('organisations.form.sections.context')}</h3>
        <label className="form-field">
          <span className="form-field__label">{t('organisations.form.notes')}</span>
          <textarea
            className="form-field__textarea"
            value={notes}
            placeholder={t('organisations.form.notesPlaceholder')}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
          />
        </label>
      </div>

      {error && <div className="modal__error" role="alert">{error}</div>}

      <div className="person-edit__actions">
        <button type="submit" className="button button--primary" disabled={!canSubmit}>
          {saving ? t('organisations.form.submitting') : t('organisations.form.submitEdit')}
        </button>
        <button type="button" className="button button--ghost" onClick={onCancel} disabled={saving}>
          {t('common.cancel')}
        </button>
        <button
          type="button"
          className="button button--ghost button--ghost--danger"
          onClick={onDelete}
          disabled={saving}
        >
          {t('common.delete')}
        </button>
      </div>
    </form>
  );
}

function ConfirmDeleteDialog({ name, onCancel, onConfirm, deleting }) {
  const { t } = useTranslation();
  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" onClick={() => !deleting && onCancel()}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2 className="modal__title">{t('organisations.delete.title')}</h2>
        <p className="modal__body">{t('organisations.delete.body', { name })}</p>
        <div className="modal__actions">
          <button type="button" className="button button--ghost" onClick={onCancel} disabled={deleting}>
            {t('common.cancel')}
          </button>
          <button type="button" className="button button--danger" onClick={onConfirm} disabled={deleting}>
            {t('organisations.delete.confirm')}
          </button>
        </div>
      </div>
    </div>
  );
}

export function OrganisationsList() {
  const { t } = useTranslation();
  const [filter, setFilter] = useState('all');
  const [organisations, setOrganisations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [usingMockData, setUsingMockData] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [expandedId, setExpandedId] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [editError, setEditError] = useState(null);
  const [savingId, setSavingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    const filterDef = FILTERS.find((f) => f.key === filter);
    const filters = filterDef?.type ? { type: filterDef.type } : {};
    api.organisations
      .list(filters)
      .then((data) => {
        if (cancelled) return;
        setOrganisations(data?.organisations ?? []);
        setUsingMockData(false);
      })
      .catch((err) => {
        if (cancelled) return;
        console.warn('[organisations] API unreachable, using mock data:', err.message);
        setOrganisations(
          filter === 'all'
            ? MOCK_ORGANISATIONS
            : MOCK_ORGANISATIONS.filter((o) => o.type === filterDef?.type),
        );
        setUsingMockData(true);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => { cancelled = true; };
  }, [filter, refreshKey]);

  function handleCreated() {
    setShowCreate(false);
    setRefreshKey((k) => k + 1);
  }

  async function handleSave(id, updates) {
    setEditError(null);
    setSavingId(id);
    try {
      const updated = await api.organisations.update(id, updates);
      setOrganisations((prev) => prev.map((o) => (o.id === id ? updated : o)));
      setExpandedId(null);
    } catch (err) {
      setEditError(err?.message || t('organisations.form.error'));
    } finally {
      setSavingId(null);
    }
  }

  async function handleDelete(id) {
    setDeleting(true);
    try {
      await api.organisations.delete(id);
      setOrganisations((prev) => prev.filter((o) => o.id !== id));
      setExpandedId((prev) => (prev === id ? null : prev));
      setDeleteTarget(null);
    } catch (err) {
      console.error('[organisations] delete failed:', err);
      setEditError(err?.message || t('organisations.form.error'));
    } finally {
      setDeleting(false);
    }
  }

  return (
    <>
      <div
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 'var(--space-3)', marginBottom: 'var(--space-4)' }}
      >
        <div className="page-header__subtitle">{t('organisations.subtitle')}</div>
        <button type="button" className="button button--primary" onClick={() => setShowCreate(true)}>
          + {t('organisations.addOrganisation')}
        </button>
      </div>

      <div className="filter-pills" role="tablist" style={{ marginBottom: 'var(--space-4)' }}>
        {FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            role="tab"
            aria-selected={filter === f.key}
            className={'filter-pill' + (filter === f.key ? ' filter-pill--active' : '')}
            onClick={() => { setFilter(f.key); setExpandedId(null); }}
          >
            {t(f.labelKey)}
          </button>
        ))}
      </div>

      {usingMockData && <div className="dev-notice">{t('organisations.loadError')}</div>}

      {isLoading ? (
        <div className="empty-state">
          <div className="empty-state__icon" aria-hidden="true">⏳</div>
          <p>{t('organisations.loading')}</p>
        </div>
      ) : organisations.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state__icon" aria-hidden="true">🏢</div>
          <p>{t('organisations.noResults')}</p>
        </div>
      ) : (
        <div className="org-list">
          {organisations.map((org) => {
            const isOpen = expandedId === org.id;
            const handles = Object.entries(org.socialHandles || {});
            return (
              <div key={org.id}>
                <button
                  type="button"
                  className="org-row"
                  onClick={() => {
                    setExpandedId(isOpen ? null : org.id);
                    setEditError(null);
                  }}
                  aria-expanded={isOpen}
                >
                  <div className="org-row__logo">
                    {org.logoB64 ? <img src={org.logoB64} alt="" /> : <span aria-hidden="true">🏢</span>}
                  </div>
                  <div className="org-row__body">
                    <div className="org-row__name">{org.name}</div>
                    {org.website && (
                      <div className="org-row__website">
                        <a href={org.website} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>
                          {org.website.replace(/^https?:\/\//, '')}
                        </a>
                      </div>
                    )}
                  </div>
                  <div className="org-row__social">
                    {handles.map(([platform, handle]) => (
                      <span key={platform} title={platform}>
                        <span aria-hidden="true">{SOCIAL_ICONS[platform] || '🔗'}</span>{' '}{handle}
                      </span>
                    ))}
                  </div>
                  <span className={`org-type-chip org-type-chip--${org.type}`}>
                    {t(`organisations.types.${org.type}`)}
                  </span>
                </button>
                {isOpen && (
                  <OrganisationEditForm
                    organisation={org}
                    onSave={(updates) => handleSave(org.id, updates)}
                    onCancel={() => { setExpandedId(null); setEditError(null); }}
                    onDelete={() => setDeleteTarget(org)}
                    saving={savingId === org.id}
                    error={editError}
                  />
                )}
              </div>
            );
          })}
        </div>
      )}

      {showCreate && (
        <CreateOrganisationModal
          onClose={() => setShowCreate(false)}
          onCreated={handleCreated}
        />
      )}

      {deleteTarget && (
        <ConfirmDeleteDialog
          name={deleteTarget.name}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={() => handleDelete(deleteTarget.id)}
          deleting={deleting}
        />
      )}
    </>
  );
}
